import type { TimeEntryDto, TimeEntryWarning } from '@fos/shared/contracts/time';
import { useQueryClient } from '@tanstack/react-query';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { toast } from '../../../ui/Toast';

/**
 * Zapis wpisów czasu z UI: odświeżenie list (czas, projekty — wykorzystanie budżetu godzin), ostrzeżenia
 * z odpowiedzi (nakładanie się, klient bez stawki) jako toast, usunięcie z „Cofnij”.
 */

export function useInvalidateTime() {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      utils.time.invalidate(),
      utils.projects.invalidate(),
      // kompozycja: kafelki zależne od wpisów (Dziś, „Do rozliczenia” i „Główna oferta” w profilu klienta)
      utils.billing.clientSummary.invalidate(),
      utils.billing.overview.invalidate(),
      utils.billing.stats.invalidate(),
      utils.offers.mainOffer.invalidate(),
      // pula godzin usług (karta klienta, Utrzymanie, chip na zadaniu) liczy się z wpisów (17.10.2026)
      utils.services.invalidate(),
      queryClient.invalidateQueries({ queryKey: ['today'] }),
    ]);
  };
}

/** Ostrzeżenia zapisu (zapis się udał): „Ten wpis nachodzi na inny Twój wpis” + lista wpisów. */
export function showWarnings(warnings: TimeEntryWarning[]): void {
  for (const w of warnings) {
    const description =
      w.code === 'OVERLAP' && w.entries?.length
        ? w.entries.map((e) => `${e.start}–${e.end}${e.description ? ` ${e.description}` : ''}`).join(', ')
        : undefined;
    toast.warning(w.message, description ? { description } : undefined);
  }
}

/** Komunikat błędu zapisu: konflikt wersji, wpis zamknięty w rozliczeniu, inne. */
export function timeErrorToast(err: unknown, fallback = 'Nie udało się zapisać wpisu.'): void {
  if (appErrorCode(err) === 'CONFLICT') {
    const message = errorMessage(err, '');
    if (message && !/zmieni|wersj/i.test(message) && message !== 'Konflikt') {
      toast.error(message);
      return;
    }
    toast.error('Wpis zmienił się w międzyczasie', { description: 'Odświeżyliśmy listę. Spróbuj jeszcze raz.' });
    return;
  }
  toast.error(errorMessage(err, fallback));
}

/** Usunięcie wpisu (archiwizacja) z „Cofnij” w toaście. */
export function useArchiveEntry() {
  const archive = trpc.time.archive.useMutation();
  const restore = trpc.time.restore.useMutation();
  const invalidate = useInvalidateTime();
  return {
    pending: archive.isPending,
    async run(entry: Pick<TimeEntryDto, 'id' | 'version' | 'description'>) {
      try {
        const archived = await archive.mutateAsync({ id: entry.id, version: entry.version });
        await invalidate();
        toast.success('Usunięto wpis', {
          description: entry.description || undefined,
          action: {
            label: 'Cofnij',
            onClick: () => {
              void restore
                .mutateAsync({ id: archived.id, version: archived.version })
                .then(invalidate)
                .catch((err) => timeErrorToast(err, 'Nie udało się przywrócić wpisu.'));
            },
          },
        });
      } catch (err) {
        if (appErrorCode(err) === 'CONFLICT') await invalidate();
        timeErrorToast(err, 'Nie udało się usunąć wpisu.');
      }
    },
  };
}
